"use client";

import Link from "next/link";
import { motion } from "framer-motion";
import Image, { type StaticImageData } from "next/image";
import { excursionsCards } from "@/(global)/excursions/slug-excursions";
import type { Locale } from "@/config/i18n";
import { tr } from "@/config/localeText";
import { LANDING } from "./landingDict";

type ExcursionItem = {
  title: string;
  href: string;
  image: StaticImageData;
};

type ExcursionsSectionProps = {
  locale?: Locale;
};

export const ExcursionsSection = ({
  locale = "fr",
}: ExcursionsSectionProps) => {
  const t = tr(LANDING, locale).excursions;
  // FR lit les cartes brutes ; EN et AR remplacent le titre par sa traduction
  // (t.cardTitles, même ordre que excursionsCards) et préfixent l'URL par la
  // locale — les slugs excursion sont identiques FR↔EN↔AR. NB : avant, seul
  // "en" était géré → l'arabe retombait sur les titres + liens FR.
  const excursions: ExcursionItem[] =
    locale === "fr"
      ? excursionsCards
      : excursionsCards.map((card, index) => ({
          ...card,
          title: t.cardTitles?.[index] ?? card.title,
          href: `/${locale}${card.href}`,
        }));
  return (
    <section className="w-full bg-white py-16 text-black">
      <div className="mx-auto max-w-7xl px-4">
        <motion.h2
          initial={false}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ once: true, margin: "-10%" }}
          className="mb-8 text-center font-space-age text-2xl tracking-[0.25em] text-black"
        >
          {t.heading}
        </motion.h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {excursions.map((e) => (
            <motion.div
              key={e.title}
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-15%" }}
              transition={{ duration: 0.45 }}
              className="group overflow-hidden rounded-2xl border border-black/15 bg-black/[0.02] p-5 hover:border-primary"
            >
              <div className="relative mb-3 aspect-[16/9] overflow-hidden rounded-xl">
                <Image
                  src={e.image}
                  alt={e.title}
                  fill
                  sizes="(max-width: 1024px) 100vw, 25vw"
                  className="object-cover transition-transform duration-500 group-hover:scale-105"
                  priority={false}
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
              </div>
              <h3 className="mb-2 text-lg font-semibold">{e.title}</h3>
              <Link
                href={e.href}
                className="text-sm text-primary group-hover:underline"
              >
                {t.discover}
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
